/**
 * Catálogos y reglas del nodo `cfdi:InformacionGlobal` de un CFDI 4.0.
 *
 * El nodo describe el periodo que consolida una factura global (la que se emite a
 * público en general). Solo tiene sentido con el RFC genérico `XAXX010101000`: con un
 * RFC real el SAT lo rechaza, por eso la captura se muestra únicamente en ese caso.
 */
export const GLOBAL_RECEIVER_RFC = 'XAXX010101000';

export const isGlobalReceiver = (rfc?: string): boolean =>
  (rfc ?? '').trim().toUpperCase() === GLOBAL_RECEIVER_RFC;

/** c_Periodicidad. */
export const PERIODICITY_OPTIONS = [
  { code: '01', name: 'Diaria' },
  { code: '02', name: 'Semanal' },
  { code: '03', name: 'Quincenal' },
  { code: '04', name: 'Mensual' },
  { code: '05', name: 'Bimestral' },
] as const;

export const PERIODICITY_BIMONTHLY = '05';

/** La periodicidad bimestral solo la admite el SAT a emisores del RIF (régimen 621). */
export const BIMONTHLY_ISSUER_REGIME = '621';

export const periodicityOptionsFor = (issuerRegime?: string) =>
  issuerRegime === BIMONTHLY_ISSUER_REGIME
    ? PERIODICITY_OPTIONS
    : PERIODICITY_OPTIONS.filter((option) => option.code !== PERIODICITY_BIMONTHLY);

/** c_Meses, 01-12. */
const MONTH_OPTIONS = [
  { code: '01', name: 'Enero' },
  { code: '02', name: 'Febrero' },
  { code: '03', name: 'Marzo' },
  { code: '04', name: 'Abril' },
  { code: '05', name: 'Mayo' },
  { code: '06', name: 'Junio' },
  { code: '07', name: 'Julio' },
  { code: '08', name: 'Agosto' },
  { code: '09', name: 'Septiembre' },
  { code: '10', name: 'Octubre' },
  { code: '11', name: 'Noviembre' },
  { code: '12', name: 'Diciembre' },
];

/** c_Meses, 13-18: los bimestres. */
const BIMESTER_OPTIONS = [
  { code: '13', name: 'Enero - Febrero' },
  { code: '14', name: 'Marzo - Abril' },
  { code: '15', name: 'Mayo - Junio' },
  { code: '16', name: 'Julio - Agosto' },
  { code: '17', name: 'Septiembre - Octubre' },
  { code: '18', name: 'Noviembre - Diciembre' },
];

/**
 * Los meses válidos dependen de la periodicidad: bimestral usa los códigos 13-18 y
 * cualquier otra los 01-12. Ofrecer solo la lista correcta es lo que evita la
 * combinación inválida sin necesidad de una validación cruzada aparte.
 */
export const monthOptionsFor = (periodicity: string) =>
  periodicity === PERIODICITY_BIMONTHLY ? BIMESTER_OPTIONS : MONTH_OPTIONS;

/** Mes o bimestre en curso, para arrancar la captura en el periodo más probable. */
export const currentMonthCode = (periodicity: string, now = new Date()): string => {
  const month = now.getMonth() + 1;
  if (periodicity === PERIODICITY_BIMONTHLY) {
    return String(12 + Math.ceil(month / 2)).padStart(2, '0');
  }
  return String(month).padStart(2, '0');
};

export interface GlobalInformation {
  periodicity: string;
  month: string;
  year: number;
}

export const defaultGlobalInformation = (now = new Date()): GlobalInformation => ({
  periodicity: '04',
  month: currentMonthCode('04', now),
  year: now.getFullYear(),
});

/** «Mensual · Agosto 2026»: el periodo en una línea, para el resumen y la revisión. */
export const describeGlobalPeriod = (info: GlobalInformation): string => {
  const periodicity =
    PERIODICITY_OPTIONS.find((option) => option.code === info.periodicity)?.name ?? info.periodicity;
  const month =
    monthOptionsFor(info.periodicity).find((option) => option.code === info.month)?.name ?? info.month;
  return `${periodicity} · ${month} ${info.year}`;
};
